import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button } from 'antd';
import { Link } from 'react-router-dom';

import ConfirmWordModal from '../common/ConfirmWordModal';
import DialogLoadState from '../common/DialogLoadState';
import { previewBundleRemoval, removeBundle } from '../../services/packages';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';
import { EXTERNAL_LINK_PROPS } from '../common/ExternalLink';

/**
 * "Remove bundle…" from a bundle's group in the Card Library (KPR-1132; epic
 * KPE-035; the API is KPR-1131). The dialog is drawn from the preview the
 * server makes (nothing is written to read it): what goes, by kind, with the
 * cards listed and their record counts; what stays, each with its reason; and
 * what blocks the removal, each naming what to change first. While anything
 * blocks, the red button stays disarmed. Otherwise the person types `remove`
 * (ConfirmWordModal, the platform's typed-word confirm).
 *
 * The preview's `previewToken` is sent with the remove. A removal whose
 * answer is `remove_preview_stale` (something changed since) reads the
 * preview again and says so, and a failure part-way shows what was removed
 * and what is left, with Try again.
 */

const KINDS = [
	[ 'cards', 'card', 'cards' ],
	[ 'elementSets', 'element set', 'element sets' ],
	[ 'tags', 'tag', 'tags' ],
	[ 'tagRules', 'tag rule', 'tag rules' ],
	[ 'layouts', 'layout', 'layouts' ],
	[ 'savedFilters', 'saved filter', 'saved filters' ],
	[ 'quickAdds', 'quick add', 'quick adds' ],
	[ 'multiAdds', 'multi add', 'multi adds' ],
	[ 'charts', 'chart', 'charts' ],
	[ 'dashboards', 'dashboard', 'dashboards' ],
	[ 'automations', 'rule', 'rules' ],
	[ 'notifications', 'notification', 'notifications' ],
	// The forms its install prepared (keepr-api KPR-360): each is retired.
	[ 'publicForms', 'form', 'forms' ],
];

const count = (n, one, many) => `${ n } ${ n === 1 ? one : many }`;
const quote = name => ( name ? `“${ name }”` : 'a card you cannot open' );

/** The kinds that have something in them, each `{ key, label, rows }`. */
export function goesByKind( goes ) {
	return KINDS
		.map( ( [ key, one, many ] ) => ( { key, label: count( ( goes?.[ key ] || [] ).length, one, many ), rows: goes?.[ key ] || [] } ) )
		.filter( kind => kind.rows.length );
}

/** How many records go with the cards. */
export function itemTotal( goes ) {
	return ( goes?.cards || [] ).reduce( ( sum, card ) => sum + ( Number( card.items ) || 0 ), 0 );
}

function cardLink( card ) {
	if ( ! card?.id ) return null;
	return <Link to={`/cards/${ card.id }`} {...EXTERNAL_LINK_PROPS}>Open {quote( card.name )}</Link>;
}

/** One stay as a sentence, with the reason. */
export function stayLine( stay ) {
	const card = stay.card ? quote( stay.card.name ) : 'A card you cannot open';
	switch ( stay.reason ) {
		case 'links_into': {
			const into = ( stay.into || [] ).map( c => quote( c.name ) ).join( ', ' );
			return `${ card } stays. It has a link into ${ into || 'a card that goes' }${ stay.items ? `, held by ${ count( stay.items, 'record', 'records' ) }` : '' }; those links keep their values.`;
		}
		case 'extends': return `${ card } stays. It extends ${ quote( stay.of?.name ) } and loses what it inherits from it.`;
		case 'moved': return `${ card } stays. It was moved to another collection.`;
		case 'not_from_this_install': return stay.tag
			? `The tag ${ quote( stay.tag.name ) } stays. It was here before the bundle was added.`
			: `${ card } stays. The bundle did not make it.`;
		case 'carried': return `The element set ${ quote( stay.elementSet?.name ) } stays. ${ ( stay.cards || [] ).map( c => quote( c.name ) ).join( ', ' ) || 'A card that stays' } uses it.`;
		case 'personal': return 'A setting someone made their own stays.';
		case 'on_your_items': return `The tag ${ quote( stay.tag?.name ) } stays. It is on ${ count( stay.items || 0, 'record', 'records' ) } of cards that stay.`;
		default:
			return stay.tag
				? `The tag ${ quote( stay.tag.name ) } stays. Removing it would take it off records or settings that still use it.`
				: 'This stays, because something you made depends on it.';
	}
}

/** One blocker: what it is, what to change first, and where to open it. */
export function blockerView( blocker ) {
	switch ( blocker.code ) {
		case 'card_in_related_lists': {
			const card = blocker.card;
			const list = blocker.list?.listName ? ` “${ blocker.list.listName }”` : '';
			return {
				text: `${ quote( card?.name ) } has a related list${ list } that shows a card from this bundle. Remove that list first.`,
				link: cardLink( card ),
			};
		}
		case 'card_in_use':
			return {
				text: `${ quote( blocker.card?.name ) } is looked up from another collection. Take that lookup out first.`,
				link: cardLink( blocker.card ),
			};
		case 'element_set_in_use':
			return { text: `The element set ${ quote( blocker.elementSet?.name ) } is used by another collection. Take that use out first.`, link: null };
		// KPR-360: the bundle's forms are retired, which asks for a confirmed address.
		case 'email_unverified':
			return { text: 'Removing this bundle retires its forms. Confirm your email address first, from the link keepr sent you or your profile.', link: null };
		default:
			return { text: 'Something in the bundle is in use elsewhere. Resolve it first.', link: null };
	}
}

function GoesList( { goes } ) {
	const kinds = goesByKind( goes );
	if ( ! kinds.length ) return <p>Nothing else is left of this bundle here.</p>;
	return (
		<ul className='remove-bundle__goes' data-testid='remove-bundle-goes'>
			{ kinds.map( kind => (
				<li key={kind.key}>
					<span className='remove-bundle__kind'>{ kind.label }</span>
					{ kind.key === 'cards' ? (
						<ul className='remove-bundle__cards'>
							{ kind.rows.map( row => (
								<li key={row.id}>{ row.name || 'Untitled card' } <span className='remove-bundle__muted'>{ count( row.items || 0, 'record', 'records' ) }</span></li>
							) ) }
						</ul>
					) : null }
					{ /* KPR-360: a form made live since is retired for good — said by name. */ }
					{ kind.key === 'publicForms' && kind.rows.some( row => row.state === 'live' || row.state === 'paused' ) ? (
						<ul className='remove-bundle__cards' data-testid='remove-bundle-live-forms'>
							{ kind.rows.filter( row => row.state === 'live' || row.state === 'paused' ).map( row => (
								<li key={row.id}>{ quote( row.name ) } is live. Removing the bundle retires it for good: its address stops taking submissions.</li>
							) ) }
						</ul>
					) : null }
				</li>
			) ) }
		</ul>
	);
}

function PartialNotice( { partial, onRetry } ) {
	const removed = goesByKind( partial.removed );
	const left = goesByKind( partial.left );
	const failed = partial.failed;
	return (
		<Alert
			type='warning'
			showIcon
			className='remove-bundle__partial'
			message='The bundle was only partly removed'
			description={(
				<div>
					{ removed.length ? <p>Removed: { removed.map( k => k.label ).join( ', ' ) }.</p> : <p>Nothing was removed.</p> }
					{ left.length ? <p>Left: { left.map( k => k.label ).join( ', ' ) }.</p> : null }
					{ failed?.name ? <p>It stopped at { quote( failed.name ) }{ failed.reason ? `: ${ failed.reason }` : '' }.</p> : null }
					<Button size='small' onClick={onRetry}>Try again</Button>
				</div>
			)}
		/>
	);
}

export default function RemoveBundleDialog( { bundleKey, bundleName, collectionId, onClose, onRemoved } ) {
	const { message } = App.useApp();
	const [ preview, setPreview ] = useState( null );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( false );
	const [ busy, setBusy ] = useState( false );
	const [ partial, setPartial ] = useState( null );
	const [ again, setAgain ] = useState( 0 );
	const [ note, setNote ] = useState( null );

	useEffect( () => {
		let live = true;
		setLoading( true );
		setError( false );
		previewBundleRemoval( collectionId, bundleKey )
			.then( data => { if ( live ) { setPreview( data ); setLoading( false ); } } )
			.catch( err => { if ( live ) { setError( apiErrorMessage( err, 'Unable to load what removing the bundle would take.' ) ); setLoading( false ); } } );
		return () => { live = false; };
	}, [ collectionId, bundleKey, again ] );

	const reload = useCallback( () => setAgain( n => n + 1 ), [] );

	const blockers = preview?.blockers || [];
	const blocked = blockers.length > 0;
	const items = itemTotal( preview?.goes );
	const name = preview?.package?.name || bundleName || 'this bundle';

	const confirm = async () => {
		if ( ! preview || blocked || busy ) return;
		setBusy( true );
		try {
			await removeBundle( collectionId, bundleKey, preview.previewToken );
			message.success( `${ name } removed.` );
			onRemoved?.();
			onClose();
		} catch ( err ) {
			const code = apiErrorCode( err );
			const data = err?.response?.data || {};
			if ( data.partial ) {
				setPartial( data.partial );
				setNote( null );
				onRemoved?.( { partial: true } );
				reload();
			} else if ( code === 'remove_preview_stale' ) {
				setNote( 'The bundle changed while this was open, so the list below is read again. Check it, then confirm.' );
				reload();
			} else if ( code === 'remove_blocked' ) {
				setNote( null );
				reload();
			} else {
				message.error( apiErrorMessage( err, 'The bundle could not be removed. Nothing was changed.' ) );
			}
		} finally {
			setBusy( false );
		}
	};

	const stays = preview?.stays || [];
	return (
		<ConfirmWordModal
			open
			word='remove'
			title={`Remove ${ name }`}
			okText='Remove bundle'
			onOk={confirm}
			onCancel={onClose}
			extraValid={Boolean( preview ) && ! blocked && ! loading && ! error}
			busy={busy}
		>
			<div className='remove-bundle' data-testid='remove-bundle'>
				<DialogLoadState
					loaded={Boolean( preview )}
					loading={loading}
					error={Boolean( error )}
					errorDetail={typeof error === 'string' ? error : undefined}
					label='what removing the bundle takes'
					onRetry={reload}
				>
					{ preview ? (
						<>
							{ note ? <Alert type='info' showIcon message={note} className='remove-bundle__note' /> : null }
							{ partial ? <PartialNotice partial={partial} onRetry={() => { setPartial( null ); reload(); }} /> : null }
							{ blocked ? (
								<Alert
									type='error'
									showIcon
									className='remove-bundle__blockers'
									data-testid='remove-bundle-blockers'
									message='Fix these first. Until then the bundle cannot be removed.'
									description={(
										<ul>
											{ blockers.map( ( blocker, index ) => {
												const view = blockerView( blocker );
												return <li key={`${ blocker.code }-${ index }`}>{ view.text } { view.link }</li>;
											} ) }
										</ul>
									)}
								/>
							) : null }
							<h4 className='remove-bundle__heading'>What goes</h4>
							<GoesList goes={preview.goes} />
							{ stays.length ? (
								<>
									<h4 className='remove-bundle__heading'>What stays</h4>
									<ul className='remove-bundle__stays' data-testid='remove-bundle-stays'>
										{ stays.map( ( stay, index ) => <li key={`${ stay.reason }-${ index }`}>{ stayLine( stay ) }</li> ) }
									</ul>
								</>
							) : null }
							<p className='remove-bundle__sentence' data-testid='remove-bundle-sentence'>
								{ items > 0
									? `This also removes ${ count( items, 'record', 'records' ) } held in these cards. You can restore the cards from History, and their records for 30 days.`
									: 'You can restore a removed card from History.' }
							</p>
						</>
					) : null }
				</DialogLoadState>
			</div>
		</ConfirmWordModal>
	);
}
